import { OnDestroy, Directive } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';
import { CustomerSubscriptionsService, Subscription } from 'ldt-billing-service-api';
import { AuthService } from 'src/app/auth/service/auth.service';
import { NotificationService } from 'src/app/shared/notification-service/notification.service';
import { BillingService } from 'src/app/core/services/billing.service';
import { Log } from 'src/app/logger.service';

@Directive() // Angular requires @Directive or @Component for DI and lifecycle hooks on base classes
export abstract class BaseProductSubscriptionComponent implements OnDestroy {
  public currentSubscription: Subscription | null = null;
  // Every subscription the org holds for this product. There can be more than one
  // (e.g. an active plan plus an older plan that's still active but scheduled to
  // cancel at term end), so subclasses can inspect each tier's true state.
  public allProductSubscriptions: Subscription[] = [];
  public isLoadingSubscriptions = true;
  public isPollingForUpdate = false;
  protected ngUnsubscribe = new Subject<void>();
  protected pollingUnsubscribe = new Subject<void>();

  protected abstract readonly productId: string;

  constructor(
    protected subscriptionsService: CustomerSubscriptionsService,
    protected authService: AuthService,
    protected notificationService: NotificationService,
    protected billingService: BillingService
  ) {}

  // Gets the subscription for the current product and calls back to the subclass to handle the subscription loaded event
  protected initializeBaseSubscriptionLogic(): void {
    this.isLoadingSubscriptions = true;
    this.billingService.subscriptions$.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (subscriptions) => {
        if (subscriptions === null) {
          this.isLoadingSubscriptions = true;
          this.currentSubscription = null;
          this.allProductSubscriptions = [];
        } else {
          const productSubs = subscriptions.filter((s) => s.product?.id === this.productId);
          this.allProductSubscriptions = productSubs;
          // Prefer an active subscription that is NOT scheduled to cancel; only fall
          // back to a canceling/other one so the "current plan" reflects reality.
          this.currentSubscription =
            productSubs.find((s) => this.isActive(s) && !s.endsAt) ??
            productSubs.find((s) => this.isActive(s)) ??
            productSubs[0] ??
            null;
          this.isLoadingSubscriptions = false;
          this.onSubscriptionLoaded();
        }
      },
      error: (err) => {
        Log.e(
          `BaseProductSubscriptionComponent: Error fetching subscriptions for product ${this.productId}:`,
          err
        );
        this.notificationService.error(
          'Error loading your subscription details. Please try again later.'
        );
        this.isLoadingSubscriptions = false;
      },
    });
  }

  protected abstract onSubscriptionLoaded(): void;

  // Stripe-style "active" status (kept loose since status is a free-form string).
  protected isActive(sub: Subscription): boolean {
    return sub?.status?.toLowerCase() === 'active';
  }

  // A subscription that is currently active but scheduled to cancel at term end
  // (active + an endsAt date). It still grants access until then.
  public isCanceling(sub: Subscription | null | undefined): boolean {
    return !!sub && this.isActive(sub) && !!sub.endsAt;
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.pollingUnsubscribe.next();
    this.pollingUnsubscribe.complete();
  }

  public cancelSubscription(): void {
    const link = document.createElement('a');
    this.subscriptionsService
      .createPortalSession(this.authService.getSelectedOrgIdValue)
      .subscribe({
        next: (session) => {
          if (session.sessionUrl) {
            link.href = session.sessionUrl;
            link.click();
          } else {
            Log.e('BaseProductSubscriptionComponent: Portal session URL is missing.');
            this.notificationService.error(
              'Could not connect to Stripe. Please try again later or contact support.'
            );
          }
        },
        error: (err) => this.handleBillingError(err, 'redirecting you to Stripe'),
      });
  }

  // After a subscription change, we want to poll our backend billing service until the subscription is updated
  // Different products do different checks to determine if the subscription is updated
  protected pollUntilConditionMet(
    condition: () => boolean,
    successMessage: string,
    pollingIntervalMs: number = 2000
  ): void {
    this.isPollingForUpdate = true;
    // Initial refresh call
    this.billingService.refreshSubscriptions();

    const intervalId = setInterval(() => {
      if (this.currentSubscription !== undefined) {
        if (condition()) {
          clearInterval(intervalId);

          // This ensures new licenses and limits are applied
          this.authService.refreshSession();
          this.isPollingForUpdate = false;
          if (successMessage) {
            this.notificationService.success(successMessage);
          }
          Log.i('BaseProductSubscriptionComponent: Polling condition met, subscription updated.');
          this.pollingUnsubscribe.next();
        } else {
          this.billingService.refreshSubscriptions();
        }
      }
    }, pollingIntervalMs);

    this.pollingUnsubscribe
      .pipe(
        filter(() => !this.pollingUnsubscribe.closed),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        clearInterval(intervalId);
      });
  }

  // For checkout, we'll take over the window since Stripe brings them back to us
  protected handleStripeCheckoutSession(sessionUrl: string | undefined): void {
    if (sessionUrl) {
      window.location.href = sessionUrl;
    } else {
      Log.e('BaseProductSubscriptionComponent: Checkout session URL is missing.');
      this.notificationService.error('Could not retrieve checkout session URL. Please try again.');
    }
  }

  protected handleBillingError(err: any, contextMessage: string): void {
    this.notificationService.error(
      `Oops. Problem ${contextMessage}. Please try again later or contact support.`
    );
    Log.e(`BaseProductSubscriptionComponent: Error ${contextMessage}:`, err);
  }
}
